import React from "react";
import { Box, FormHelperText, useMediaQuery, useTheme } from "@mui/material";
import { ControllerRenderProps } from "react-hook-form";
import { dt } from "../../../themes/redesignTokens";

export type CardVariant = "centered" | "horizontal";

export interface RadioCardOption<T extends string | number = number> {
  code: T;
  icon: React.ReactNode;
  label: string;
  description?: string;
  color?: string;
  colorSoft?: string;
  colorGlow?: string;
}

export interface FormRadioCardSelectorProps<T extends string | number = number> {
  options: RadioCardOption<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
  // react-hook-form mode
  field?: ControllerRenderProps<Record<string, unknown>, string>;
  // layout
  variant?: CardVariant;
  error?: string;
}

/**
 * Larger selectable card group for mutually exclusive options with description text.
 *
 * Two layout variants extracted from RFS:
 *   - "centered": icon circle centered above label + description (service type cards)
 *   - "horizontal": icon circle left of label + description with left border accent (urgency cards)
 *
 * Works with both react-hook-form Controller and standalone onChange.
 */
function FormRadioCardSelector<T extends string | number = number>({
  options,
  value,
  onChange,
  field,
  variant = "centered",
  error,
}: FormRadioCardSelectorProps<T>) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const resolvedValue = field ? (field.value as T | undefined) : value;

  const handleSelect = (code: T) => {
    if (field) {
      field.onChange(code);
    }
    onChange(code);
  };

  const gridColumns = isPhone
    ? "1fr"
    : isTablet
    ? "repeat(2, 1fr)"
    : `repeat(${Math.min(options.length, 3)}, 1fr)`;

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridColumns,
          gap: 2,
        }}
      >
        {options.map((option, index) => {
          const selected = resolvedValue === option.code;
          const accentColor = option.color || dt.blue;
          const softColor = option.colorSoft || dt.blueSoft;
          const glowColor = option.colorGlow || dt.blueGlow;

          if (variant === "horizontal") {
            return (
              <Box
                key={String(option.code)}
                onClick={() => handleSelect(option.code)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  padding: "16px",
                  borderRadius: dt.radiusSm,
                  cursor: "pointer",
                  border: selected
                    ? `2px solid ${accentColor}`
                    : `1px solid ${dt.cardBorder}`,
                  borderLeft: selected
                    ? `4px solid ${accentColor}`
                    : `4px solid transparent`,
                  backgroundColor: selected ? softColor : dt.cardBg,
                  boxShadow: selected
                    ? `0 0 0 3px ${glowColor}`
                    : dt.cardShadow,
                  transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                  animation: `fadeUp 0.3s ease ${index * 0.05}s both`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: dt.cardShadowHover,
                    borderColor: accentColor,
                  },
                }}
              >
                {/* Icon circle */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: selected ? `${accentColor}15` : dt.inputBg,
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                      color: selected ? accentColor : dt.textMuted,
                      transition: "color 0.2s ease",
                    },
                  }}
                >
                  {option.icon}
                </Box>

                {/* Text content */}
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "14px",
                      fontWeight: selected ? 600 : 500,
                      color: selected ? accentColor : dt.textPrimary,
                      transition: "color 0.2s ease",
                    }}
                  >
                    {option.label}
                  </Box>
                  {option.description && (
                    <Box
                      sx={{
                        fontFamily: dt.font,
                        fontSize: "12px",
                        color: dt.textMuted,
                        mt: 0.25,
                        lineHeight: 1.4,
                      }}
                    >
                      {option.description}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          }

          // variant === "centered" (default)
          return (
            <Box
              key={String(option.code)}
              onClick={() => handleSelect(option.code)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                padding: "20px 16px",
                borderRadius: dt.radiusSm,
                cursor: "pointer",
                border: selected
                  ? `2px solid ${accentColor}`
                  : `1px solid ${dt.cardBorder}`,
                backgroundColor: selected ? softColor : dt.cardBg,
                boxShadow: selected
                  ? `0 0 0 3px ${glowColor}`
                  : dt.cardShadow,
                transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                animation: `fadeUp 0.3s ease ${index * 0.05}s both`,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: dt.cardShadowHover,
                  borderColor: accentColor,
                },
              }}
            >
              {/* Icon circle */}
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: selected ? `${accentColor}15` : dt.inputBg,
                  mb: 1.5,
                  transition: "all 0.2s ease",
                  "& .MuiSvgIcon-root": {
                    fontSize: 24,
                    color: selected ? accentColor : dt.textMuted,
                    transition: "color 0.2s ease",
                  },
                }}
              >
                {option.icon}
              </Box>

              {/* Label */}
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "14px",
                  fontWeight: selected ? 600 : 500,
                  color: selected ? accentColor : dt.textPrimary,
                  transition: "color 0.2s ease",
                }}
              >
                {option.label}
              </Box>

              {/* Description */}
              {option.description && (
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    color: dt.textMuted,
                    mt: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  {option.description}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      {error && (
        <FormHelperText error sx={{ mt: 0.5, fontFamily: dt.font }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
}

export default FormRadioCardSelector;
